import { useEffect, useMemo, useRef, useState } from 'react';
import { useOrderWorkspaceSession, boundedOrderRead } from './useOrderWorkspaceSession';
import useDesktopDeviceStatus, { getDesktopDeviceStatusSnapshot } from './useDesktopDeviceStatus';
import { getCachedOrderDeleteRequests, getCachedOrderFieldConflicts,
  getCachedOrderStatusConflicts, getLocalReconciliationQueue } from '@/api/orders';
import { getCachedRunProposalReviewItems, getOfflineDraftRunReviewItems } from '@/api/runs';
import { consumeReviewQueueDirty, countReviewQueueItems, getReviewQueueVersion,
  isReviewQueueDirty, subscribeToReviewQueueChanges } from '@/utils/reviewQueueEvents';

const queues = [getLocalReconciliationQueue, getCachedOrderStatusConflicts,
  getCachedOrderFieldConflicts, getCachedOrderDeleteRequests,
  getOfflineDraftRunReviewItems, getCachedRunProposalReviewItems];

// An informational reader only: Review retains mutation and durable settlement ownership.
export default function useReviewCount(pathname) {
  const session = useOrderWorkspaceSession();
  const device = useDesktopDeviceStatus();
  const bridge = typeof window === 'undefined' ? null : window.lvbDesktop;
  const eligible = !!bridge && session.valid && session.isAdmin && !session.offline
    && device?.registered === true && device?.isPrimary === true;
  const latestSession = useRef(session); latestSession.current = session;
  const owner = useMemo(() => ({ key: session.key, bridge, device, eligible, active: false, request: null }), [session.key, bridge, device, eligible]);
  const latest = useRef(owner); latest.current = owner;
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!eligible) return undefined;
    owner.active = true;
    const captured = latestSession.current.capture(true);
    const current = () => owner.active && latest.current === owner && captured?.current()
      && window.lvbDesktop === bridge && getDesktopDeviceStatusSnapshot(bridge) === device;
    let running = false;
    let queued = false;
    let coveredVersion = -1;
    const controllers = new Set();
    const publish = (phase, count = null) => {
      if (current()) setResult({ owner, phase, count });
    };
    const request = async () => {
      if (!current()) return;
      const version = getReviewQueueVersion();
      if (running) {
        if (version > coveredVersion) { queued = true; publish('loading'); }
        return;
      }
      running = true;
      queued = false;
      coveredVersion = version;
      publish('loading');
      let binding;
      const acceptBinding = response => {
        if (!current()) throw new Error('Review count retired');
        const next = response.headers?.get?.('X-LVB-Cache-Binding')
          ?? response.headers?.['x-lvb-cache-binding'] ?? null;
        if (next !== null && (typeof next !== 'string' || !/^[a-f0-9]{64}$/.test(next))) throw new Error('Invalid cache identity');
        if (binding !== undefined && binding !== next) throw new Error('Review cache changed');
        binding = next;
      };
      try {
        const config = { ...captured.config,
          reviewLocalBase: captured.endpoint.replace(/\/api$/, ''), reviewResponse: acceptBinding };
        const groups = await Promise.all(queues.map(read => boundedOrderRead(options => {
          if (!current()) throw new Error('Review count retired');
          return read(options);
        }, config, controllers)));
        if (groups.some(group => !Array.isArray(group))) throw new Error('Invalid review queue');
        if (!current()) return;
        consumeReviewQueueDirty(version);
        if (getReviewQueueVersion() === version) publish('ready', countReviewQueueItems(...groups));
      } catch {
        publish('unavailable');
      } finally {
        controllers.forEach(controller => controller.abort());
        running = false;
        // Only a new trigger received during this read may request a follow-up.
        // Merely remaining dirty after failure never creates a retry loop.
        if (queued && current()) { queued = false; request(); }
      }
    };
    owner.request = request;
    const unsubscribe = subscribeToReviewQueueChanges(request);
    if (captured) request();
    else setResult({ owner, phase: 'unavailable', count: null });
    return () => {
      owner.active = false;
      owner.request = null;
      unsubscribe();
      controllers.forEach(controller => controller.abort());
    };
  }, [owner, bridge, device, eligible]);

  useEffect(() => {
    if (isReviewQueueDirty()) owner.request?.();
  }, [owner, pathname]);
  if (!eligible) return { phase: 'unsupported', count: null };
  if (result?.owner !== owner) return { phase: 'loading', count: null };
  return { phase: result.phase, count: result.count };
}
